import * as SelectPrimitive from '@radix-ui/react-select'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { glassSurfaceClassName } from './glass.ts'
import { Icon } from './icon.tsx'

/**
 * Select — a labelled dropdown on the Radix base (`@radix-ui/react-select`):
 * a focus-managed listbox with full keyboard + ARIA (the trigger is a
 * `role="combobox"`, the panel a `role="listbox"`), typeahead, and an
 * outside-click / esc dismissal. Compose: `Select` (root, controlled via
 * `value`/`onValueChange` or uncontrolled via `defaultValue`) wrapping a
 * `SelectTrigger` (holding a `SelectValue`) and a `SelectContent` of
 * `SelectItem`s — optionally bucketed into `SelectGroup`s with a `SelectLabel`
 * and split by a `SelectSeparator`.
 *
 * The trigger mirrors `Input`'s surface, type size and `focus-cosy` outline,
 * and honours the same invalid-state contract: `aria-invalid` repaints the
 * border with `--input-invalid`. Its chevron is the sprite's `chevron-down`.
 * The panel is glass, like `Card variant="glass"`; the highlighted item takes
 * `brand-soft` and the selected item shows a brand check.
 *
 * Like the other overlay primitives (Dialog, Tooltip), the content is rendered
 * in place rather than through a `Portal`, so the live `/styleguide` route can
 * snapshot the open panel (the snapshot reads each specimen's own DOM subtree).
 */
function Select(props: React.ComponentProps<typeof SelectPrimitive.Root>) {
	return <SelectPrimitive.Root data-slot="select" {...props} />
}

function SelectGroup(
	props: React.ComponentProps<typeof SelectPrimitive.Group>,
) {
	return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue(
	props: React.ComponentProps<typeof SelectPrimitive.Value>,
) {
	return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

function SelectTrigger({
	className,
	children,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
	return (
		<SelectPrimitive.Trigger
			data-slot="select-trigger"
			className={cn(
				// Mirror the Input surface: same fill, border, radius, height,
				// padding, type size, focus ring, invalid border and disabled look.
				'focus-cosy border-input bg-control-glass aria-[invalid]:border-input-invalid text-body-sm md:text-body-xs data-[placeholder]:text-muted-foreground flex h-10 w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:truncate',
				className,
			)}
			{...props}
		>
			{children}
			<SelectPrimitive.Icon asChild>
				<Icon
					name="chevron-down"
					size="sm"
					aria-hidden
					className="text-muted-foreground shrink-0"
				/>
			</SelectPrimitive.Icon>
		</SelectPrimitive.Trigger>
	)
}

function SelectContent({
	className,
	children,
	position = 'popper',
	...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
	return (
		<SelectPrimitive.Content
			data-slot="select-content"
			position={position}
			className={cn(
				glassSurfaceClassName,
				'text-popover-foreground shadow-brand-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-xl border p-1',
				position === 'popper' &&
					'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
				className,
			)}
			{...props}
		>
			<SelectPrimitive.Viewport
				className={cn(
					position === 'popper' &&
						'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
				)}
			>
				{children}
			</SelectPrimitive.Viewport>
		</SelectPrimitive.Content>
	)
}

function SelectLabel({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
	return (
		<SelectPrimitive.Label
			data-slot="select-label"
			className={cn(
				'text-muted-foreground text-body-2xs px-2 py-1.5 font-semibold',
				className,
			)}
			{...props}
		/>
	)
}

function SelectItem({
	className,
	children,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
	return (
		<SelectPrimitive.Item
			data-slot="select-item"
			className={cn(
				// Radix moves DOM focus onto the highlighted item, so the focus
				// background is the highlight.
				'focus:bg-brand-soft text-body-xs relative flex w-full cursor-default items-center rounded-lg py-2 pr-2 pl-8 outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50',
				className,
			)}
			{...props}
		>
			<span className="absolute left-2 flex size-4 items-center justify-center">
				<SelectPrimitive.ItemIndicator>
					<Icon name="check" className="text-brand size-3.5" />
				</SelectPrimitive.ItemIndicator>
			</span>
			<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
		</SelectPrimitive.Item>
	)
}

function SelectSeparator({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
	return (
		<SelectPrimitive.Separator
			data-slot="select-separator"
			className={cn('bg-border -mx-1 my-1 h-px', className)}
			{...props}
		/>
	)
}

export {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
}
